import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_PAGE_LANGUAGE, SUPPORTED_LANGUAGES } from 'redhotpie-shared-lib/constants/constants';
import { useSelector } from 'redhotpie-shared-lib/hooks/useRedux';
import { getIsLogin } from 'redhotpie-shared-lib/selectors/loginSelector';

import { useI18n } from 'contexts/I18n/I18nContext';
import { getBrowserLanguage } from 'functions/webUtilsTypescript';
import './styles.scss';

// Shared Google Translate element engine. The widget can only be initialized
// ONCE per page load (tearing it down cannot kill its in-memory observer, and
// re-initializing stacks a second engine that double-translates), so every UI
// that triggers page translation (BrowserPageTranslator's floating button,
// TranslateItem's google-element case) must go through this single provider.
// See documents/BrowserPageTranslator_Design_And_Gotchas.md before changing.

const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-element-script';
const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
const TRANSLATE_STORAGE_KEY = 'browser-page-translator-enabled';
const TARGET_LANGUAGE_STORAGE_KEY = 'browser-page-translator-target-language';
const MAX_APPLY_RETRIES = 20;
const MAX_TRANSLATE_TRIGGER_ATTEMPTS = 2;
const TRANSLATE_SETTLE_DELAY = 1200;
const TRANSLATE_COMPLETE_TIMEOUT = 6000;
const RESTORE_SETTLE_DELAY = 1000;
const LOCALE_RERENDER_FLUSH_DELAY = 150;
const PENDING_TRANSLATE_RETRY_DELAY = 300;
// Dormant-observer nudge (gotcha #18): how long to give Google's own
// observer before checking whether late-mounted content is stuck, and the
// minimum spacing between two nudges.
const OBSERVER_NUDGE_VERIFY_DELAY = 2500;
const OBSERVER_NUDGE_COOLDOWN = 3000;
// Upper bound on tracked nudge candidates per verify window.
const OBSERVER_NUDGE_MAX_CANDIDATES = 50;

export type TranslateStatus = 'translate' | 'translating' | 'original';

// Which mechanism a translate session uses:
// - 'googleElement' — the Google Translate widget translates the rendered
//   DOM (user content and labels alike). Asynchronous, needs the engine.
// - 'i18n' — only switches the app dictionary locale (i18n.locale via
//   I18nContext.setLanguage); every I18n.t() label re-renders in place
//   through I18nRerenderBoundary, user content is left untouched.
//   Synchronous, works without the Google engine. SUPPORTED_LANGUAGES
//   matches the dictionary locales in hooks/useI18n, so any non-null
//   targetLanguage has a dictionary (missing keys fall back to English).
// - 'both' — combined flow: switch the dictionary locale FIRST (labels
//   re-render in place with the curated translations), then run the
//   google-element pass over the refreshed DOM for everything else. The
//   googtrans cookie uses 'auto' as source language, so Google detects the
//   already-localized labels as target-language text and mostly leaves them
//   alone — but that is best-effort, not guaranteed
//   (protectI18nTextFromTranslation no longer exists); opt critical labels
//   out with the notranslate class if Google mangles them.
// One session runs one engine: translatePage is a no-op while translated,
// so the engine cannot change until the page is restored.
export type TranslateEngine = 'googleElement' | 'i18n' | 'both';

let hasPatchedDomMethods = false;

// Google Translate moves text nodes into <font> wrappers, so the real DOM no
// longer matches React's virtual DOM. When React later reconciles it calls
// removeChild/insertBefore against nodes that are no longer where it left
// them and crashes with NotFoundError. This is the standard guard for the
// known React issue #11538: turn those stale calls into no-ops.
function patchDomMethodsForTranslation() {
  if (hasPatchedDomMethods) {
    return;
  }

  hasPatchedDomMethods = true;

  const originalRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function removeChildSafe<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function insertBeforeSafe<T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

// Returns the language to translate the page into, or null when translation
// is not needed (browser language matches the page's default language) or not
// possible (unsupported language).
function normalizeLanguage(language: string | undefined) {
  if (!language) {
    return undefined;
  }

  const normalized = language.toLowerCase();

  if (normalized.startsWith('zh')) {
    return normalized.includes('tw') || normalized.includes('hk') ? 'zh-TW' : 'zh-CN';
  }

  const shortLanguage = normalized.split('-')[0];

  if (shortLanguage === DEFAULT_PAGE_LANGUAGE || !SUPPORTED_LANGUAGES.includes(shortLanguage)) {
    return undefined;
  }

  return shortLanguage;
}

function getTranslateSelect() {
  return document.querySelector<HTMLSelectElement>('.goog-te-combo');
}

function triggerSelectChange(select: HTMLSelectElement) {
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function setTranslateCookie(targetLanguage: string) {
  const cookieValue = `/auto/${targetLanguage}`;
  document.cookie = `googtrans=${cookieValue}; path=/`;
  document.cookie = `googtrans=${cookieValue}; domain=${window.location.hostname}; path=/`;
}

function clearTranslateCookie() {
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const { hostname } = window.location;

  document.cookie = `googtrans=; ${expiry}; path=/`;
  document.cookie = `googtrans=; ${expiry}; domain=${hostname}; path=/`;

  // Google may set the cookie on a parent domain (e.g. .example.com for
  // app.example.com), so clear every ancestor domain level as well.
  const parts = hostname.split('.');

  for (let i = 0; i < parts.length - 1; i += 1) {
    const domain = parts.slice(i).join('.');
    document.cookie = `googtrans=; ${expiry}; domain=${domain}; path=/`;
    document.cookie = `googtrans=; ${expiry}; domain=.${domain}; path=/`;
  }
}

// Removing the widget's script/iframes does not kill the MutationObserver its
// already-loaded JS registered on the page, and that observer re-translates
// any node we restore. Google does honor the notranslate convention though,
// so marking <html> as notranslate makes the surviving observer skip
// everything until the user opts back in.
function blockPageTranslation() {
  document.documentElement.classList.add('notranslate');
  document.documentElement.setAttribute('translate', 'no');
}

function unblockPageTranslation() {
  document.documentElement.classList.remove('notranslate');
  document.documentElement.removeAttribute('translate');
}

function isPageTranslated() {
  return (
    document.documentElement.classList.contains('translated-ltr') ||
    document.documentElement.classList.contains('translated-rtl') ||
    document.body.classList.contains('translated-ltr') ||
    document.body.classList.contains('translated-rtl')
  );
}

function shouldSkipElement(element: Element | null) {
  if (!element) {
    return true;
  }

  // Content opted out of translation with class="notranslate" or
  // translate="no" — Google skips it natively, so don't snapshot/cache it
  // either. The page-level block the translator toggles on <html> during
  // restore doesn't count.
  const notranslateRoot = element.closest('.notranslate, [translate="no"]');

  if (notranslateRoot && notranslateRoot !== document.documentElement) {
    return true;
  }

  return (
    element.closest('.browser-page-translator') !== null ||
    element.closest('.skiptranslate') !== null ||
    ['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION'].includes(element.tagName)
  );
}

function collectTextNodes(root: Node) {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node => {
      const parent = node.parentElement;

      if (shouldSkipElement(parent) || !node.nodeValue?.trim()) {
        return NodeFilter.FILTER_REJECT;
      }

      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let currentNode = walker.nextNode();

  while (currentNode) {
    nodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  return nodes;
}

const NEWLINE_PRESERVING_WHITESPACE = ['pre', 'pre-wrap', 'pre-line', 'break-spaces'];

// Google Translate re-chunks text nodes and drops their newline characters,
// which destroys the layout of content that relies on white-space: pre-wrap
// (e.g. multi-line post descriptions). It does preserve element boundaries
// though, so turn semantic newlines into <br> before translating; restoring
// the original snapshot brings the plain \n text back.
function preserveLineBreaksForTranslation(root: Node = document.body) {
  collectTextNodes(root).forEach(node => {
    const parent = node.parentElement;
    const text = node.nodeValue;

    if (!parent || !text || !text.includes('\n')) {
      return;
    }

    if (!NEWLINE_PRESERVING_WHITESPACE.includes(window.getComputedStyle(parent).whiteSpace)) {
      return;
    }

    const fragment = document.createDocumentFragment();

    text.split('\n').forEach((line, index) => {
      if (index > 0) {
        fragment.appendChild(document.createElement('br'));
      }

      if (line) {
        fragment.appendChild(document.createTextNode(line));
      }
    });

    parent.replaceChild(fragment, node);
  });
}

function collectLeafTextElements(root: Node) {
  const elements: HTMLElement[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode: node => {
      if (!(node instanceof HTMLElement) || shouldSkipElement(node)) {
        return NodeFilter.FILTER_REJECT;
      }

      if (node.children.length > 0 || !node.textContent?.trim()) {
        return NodeFilter.FILTER_SKIP;
      }

      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let currentNode = walker.nextNode();

  while (currentNode) {
    elements.push(currentNode as HTMLElement);
    currentNode = walker.nextNode();
  }

  return elements;
}

// Lets a screen scope which DOM subtrees Google may translate. `allow` — only
// elements matching these CSS selectors (and their subtrees) are translated,
// everything else on the page is blocked; `deny` — elements matching these
// selectors are never translated (applied on top of `allow`, so it wins
// inside allowed subtrees too). Blocking uses the `notranslate` convention,
// which Google AND the snapshot/cache machinery already honor, so filtered
// content is never translated, snapshotted, or cached.
export interface TranslateFilter {
  allow?: string[];
  deny?: string[];
}

const FILTER_MARKER_CLASS = 'google-translate-filter-blocked';

// Selectors that must NEVER be machine-translated, on every screen and on
// top of whatever filter a screen sets (even none). Proper nouns get mangled
// by Google — user display names are the big one (class naming varies across
// the app, so match by substring). Extend this list as new cases show up;
// one-off opt-outs in a component are simpler with class="notranslate".
const DEFAULT_DENY_SELECTORS = [
  "[class*='user-name']",
  "[class*='username']",
  "[class*='display-name']",
  "[class*='club-name']",
  '.header-desktop-wrapper',
  // react-day-picker weekday header row: the 2-letter abbreviations (SU, MO,
  // TU, WE, FR...) read as English words to Google (WE -> "we", FR ->
  // "France") and come back as nonsense.
  '.rdp-head',
  // '.notification-modal',
  // '.confirm-mobile',
  // '.block-modal',
  // '.confirm-modal',
  // '.switch-gr-btn',
  // '.use-blue-button',
  // '.confirm-default-modal',
  // '.fh-comments-likes-section-mb',
];

function blockElementForFilter(element: Element) {
  // Already opted out by its owner — leave its classes alone so clearing the
  // filter cannot strip a pre-existing notranslate.
  if (element.classList.contains('notranslate')) {
    return;
  }

  element.classList.add('notranslate', FILTER_MARKER_CLASS);
}

function clearTranslateFilterMarks() {
  document.querySelectorAll(`.${FILTER_MARKER_CLASS}`).forEach(element => {
    element.classList.remove('notranslate', FILTER_MARKER_CLASS);
  });
}

// Re-applied from scratch on every translate pass (fresh, cached replay and
// the dynamic-content observer all funnel through it) so nodes mounted after
// the previous pass get filtered too, and so a changed filter fully replaces
// the marks of the old one.
function applyTranslateFilter(filter: TranslateFilter | null) {
  clearTranslateFilterMarks();

  const { allow = [], deny = [] } = filter ?? {};

  if (allow.length > 0) {
    const allowedRoots = allow.flatMap(selector => Array.from(document.body.querySelectorAll(selector)));
    const allowedSet = new Set(allowedRoots);
    // Google walks down from <body>, so the ancestor chain of every allowed
    // root must stay translatable; block every branch that forks away from
    // those chains instead of the ancestors themselves.
    const keptAncestors = new Set<Element>([document.body]);

    allowedRoots.forEach(root => {
      let ancestor = root.parentElement;

      while (ancestor && ancestor !== document.body) {
        keptAncestors.add(ancestor);
        ancestor = ancestor.parentElement;
      }
    });

    keptAncestors.forEach(ancestor => {
      Array.from(ancestor.children).forEach(child => {
        if (!keptAncestors.has(child) && !allowedSet.has(child)) {
          blockElementForFilter(child);
        }
      });
    });
  }

  [...DEFAULT_DENY_SELECTORS, ...deny].forEach(selector => {
    document.body.querySelectorAll(selector).forEach(blockElementForFilter);
  });
}

interface TranslateContextType {
  // True when the engine can be used: user logged in and their language needs
  // (and supports) translation. Trigger UIs should render null when false.
  isAvailable: boolean;
  isReady: boolean;
  status: TranslateStatus;
  targetLanguage?: string;
  defaultLanguage?: string;
  browserLanguage?: string;
  // useBrowserLanguage lets the caller pick which language to translate
  // into: true forces the visitor's browser language, false forces the
  // app's default page language (no translation), omitted keeps the
  // automatic default — the same priority I18nContext resolves its locale
  // with (?lang= param > saved cookie > browser language > English).
  // engine picks the mechanism: 'googleElement' (default, translates the
  // whole rendered DOM through the Google widget), 'i18n' (only switches
  // the app dictionary locale — I18n.t() labels re-render, user content is
  // untouched), or 'both' (i18n locale switch first, then the google pass
  // over the refreshed DOM). restorePage undoes any of them.
  translatePage: (useBrowserLanguage?: boolean, engine?: TranslateEngine) => void;
  restorePage: () => void;
  // Scope what the NEXT translate pass may touch; pass null to clear. Set it
  // in an effect and clear on unmount — it does not retroactively restore
  // content that is already translated.
  setTranslateFilter: (filter: TranslateFilter | null) => void;
}

export const TranslateContext = createContext<TranslateContextType | undefined>(undefined);

export const useTranslate = () => {
  const context = useContext(TranslateContext);

  if (!context) {
    throw new Error('useTranslate must be used within TranslateProvider');
  }

  return context;
};

interface TranslateProviderProps {
  children: ReactNode;
}

export const TranslateProvider = ({ children }: TranslateProviderProps) => {
  const { locale, setLanguage } = useI18n();
  const isLogin = useSelector(getIsLogin);
  const browserLanguage = getBrowserLanguage();
  // Which language translatePage() targets when the caller doesn't force one
  // explicitly: 'auto' follows the same priority I18nContext resolves its
  // locale with (?lang= param > saved cookie > browser language > English
  // default) via the shared `locale` value below. A caller can still force
  // either the visitor's browser language ('browser') or the app's default
  // page language / no-translate ('default') through
  // translatePage(useBrowserLanguage), which updates this so the rest of the
  // engine follows suit.
  const [languageMode, setLanguageMode] = useState<'auto' | 'browser' | 'default'>('auto');

  const sourceLanguage = languageMode === 'browser' ? browserLanguage : languageMode === 'default' ? DEFAULT_PAGE_LANGUAGE : locale;
  const targetLanguage = useMemo(() => normalizeLanguage(sourceLanguage), [sourceLanguage]);
  // The language actually being applied by the in-flight/last translation.
  // translatePage() sets this synchronously so applyTranslation/
  // waitForTranslateComplete/tryApplyCachedTranslation (which can run inside
  // timers scheduled before a languageMode-driven re-render lands) always act
  // on the language that was just requested instead of a stale closure.
  const activeTargetLanguageRef = useRef<string | undefined>(targetLanguage);
  const [isReady, setIsReady] = useState(false);
  const [status, setStatus] = useState<TranslateStatus>('translate');
  // Consumers call restorePage from unmount cleanups (effects with [] deps),
  // which freeze the callback they captured on mount. Read the live status
  // through this ref so that frozen callback still sees the current state.
  const statusRef = useRef(status);
  statusRef.current = status;
  const completionObserverRef = useRef<MutationObserver>();
  const completionTimerRef = useRef<number>();
  const hasInitializedRef = useRef(false);
  const initialHtmlLangRef = useRef(document.documentElement.lang || DEFAULT_PAGE_LANGUAGE);
  const retryTimerRef = useRef<number>();
  const mutationTimerRef = useRef<number>();
  const isApplyingRef = useRef(false);
  const isRestoringRef = useRef(false);
  const restoreTimerRef = useRef<number>();
  const lastCompletedAtRef = useRef(0);
  const originalElementTextRef = useRef<WeakMap<HTMLElement, string>>(new WeakMap());
  const originalParentContentRef = useRef<WeakMap<HTMLElement, Node[]>>(new WeakMap());
  const originalParentHtmlRef = useRef<WeakMap<HTMLElement, string>>(new WeakMap());
  const trackedTextElementsRef = useRef<HTMLElement[]>([]);
  const trackedTextParentsRef = useRef<HTMLElement[]>([]);
  const retryCountRef = useRef(0);
  // Translation cache keyed by original content (not by node), so it still
  // hits after React re-renders replace the DOM nodes with identical text.
  const elementTextCacheRef = useRef<Map<string, string>>(new Map());
  const parentHtmlCacheRef = useRef<Map<string, string>>(new Map());
  const hasTranslatedOnceRef = useRef(false);
  const completionAttemptsRef = useRef(0);
  const translateFilterRef = useRef<TranslateFilter | null>(null);
  // Retry timer for translatePage calls that arrive while the engine is in a
  // transient state (restore settling, first pass in flight). The ref to the
  // latest translatePage keeps the retry reading live state instead of the
  // stale closure that scheduled it.
  const pendingTranslateTimerRef = useRef<number>();
  const translatePageRef = useRef<(useBrowserLanguage?: boolean, engine?: TranslateEngine) => void>(() => undefined);
  // Engine of the active translate session. Set together with the status
  // transition to 'original' in translatePage and reset by restoreOriginal,
  // so effects that key on status can safely read it as a ref.
  const activeEngineRef = useRef<TranslateEngine>('googleElement');
  // Dormant-observer nudge state (gotcha #18): elements the dynamic-content
  // observer saw mount or change while translated (with the text seen at
  // that moment), pending a "did Google actually translate this?"
  // verification; the last text each node was nudged with (same text can't
  // re-nudge forever — Google deliberately keeps e.g. "OK" as-is — but a
  // label swapped to NEW text nudges again); and the timestamp of the last
  // nudge for the cooldown.
  const pendingNudgeElementsRef = useRef<{ el: HTMLElement; text: string | null }[]>([]);
  const nudgeTimerRef = useRef<number>();
  const nudgeAttemptedTextRef = useRef<WeakMap<HTMLElement, string>>(new WeakMap());
  const lastNudgeAtRef = useRef(0);

  const setTranslateFilter = useCallback((filter: TranslateFilter | null) => {
    translateFilterRef.current = filter;
  }, []);

  const snapshotOriginalText = useCallback((root: Node = document.body) => {
    collectLeafTextElements(root).forEach(element => {
      if (originalElementTextRef.current.has(element)) {
        return;
      }

      originalElementTextRef.current.set(element, element.textContent || '');
      trackedTextElementsRef.current.push(element);
    });

    collectTextNodes(root).forEach(node => {
      const parent = node.parentElement;

      if (!parent || originalParentContentRef.current.has(parent)) {
        return;
      }

      originalParentContentRef.current.set(
        parent,
        Array.from(parent.childNodes).map(child => child.cloneNode(true))
      );
      originalParentHtmlRef.current.set(parent, parent.innerHTML);
      trackedTextParentsRef.current.push(parent);
    });
  }, []);

  const restoreOriginalText = useCallback(() => {
    trackedTextElementsRef.current.forEach(element => {
      const originalText = originalElementTextRef.current.get(element);

      if (originalText !== undefined && element.isConnected) {
        element.textContent = originalText;
      }
    });

    // Google Translate replaces the original Text nodes with new nodes
    // wrapped in <font>, so restoring via the old Text node reference is a
    // no-op once it has been detached. Rebuild from the parent instead,
    // since the parent element itself stays connected.
    trackedTextParentsRef.current.forEach(parent => {
      const originalChildren = originalParentContentRef.current.get(parent);

      if (originalChildren && parent.isConnected) {
        parent.replaceChildren(...originalChildren.map(child => child.cloneNode(true)));
      }
    });
  }, []);

  const initTranslateElement = useCallback(() => {
    const translateElement = window.google?.translate?.TranslateElement;

    if (!translateElement || !document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID)) {
      return;
    }

    if (hasInitializedRef.current) {
      setIsReady(true);
      return;
    }

    hasInitializedRef.current = true;
    new translateElement(
      {
        pageLanguage: DEFAULT_PAGE_LANGUAGE,
        autoDisplay: false,
        includedLanguages: SUPPORTED_LANGUAGES.join(','),
      },
      GOOGLE_TRANSLATE_ELEMENT_ID
    );

    setIsReady(true);
  }, []);

  const loadTranslateScript = useCallback(() => {
    window.googleTranslateElementInit = initTranslateElement;

    if (window.google?.translate?.TranslateElement) {
      initTranslateElement();
      return;
    }

    if (document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }, [initTranslateElement]);

  // Boot the widget as soon as translation could be needed instead of on the
  // first translate: fetching element.js + engine init is the dominant cost
  // of that first pass (script download, iframe boot, plus the 400ms retry
  // loop waiting for the combo select), dwarfing the translateHtml call
  // itself. Booting must happen behind the <html>-level block — a leftover
  // googtrans cookie would otherwise make the engine translate the whole
  // page right on init; applyTranslation unblocks before it triggers.
  useEffect(() => {
    if (isReady || !targetLanguage) {
      return;
    }

    if (statusRef.current === 'translate' && !isApplyingRef.current) {
      blockPageTranslation();
    }

    loadTranslateScript();
  }, [isReady, loadTranslateScript, targetLanguage]);

  const captureTranslationCache = useCallback(() => {
    trackedTextElementsRef.current.forEach(element => {
      const original = originalElementTextRef.current.get(element);

      if (original === undefined || !original.trim() || !element.isConnected) {
        return;
      }

      elementTextCacheRef.current.set(original, element.textContent || '');
    });

    trackedTextParentsRef.current.forEach(parent => {
      const originalHtml = originalParentHtmlRef.current.get(parent);

      if (originalHtml === undefined || !originalHtml.trim() || !parent.isConnected) {
        return;
      }

      parentHtmlCacheRef.current.set(originalHtml, parent.innerHTML);
    });

    hasTranslatedOnceRef.current = true;
  }, []);

  const markTranslateComplete = useCallback(() => {
    completionObserverRef.current?.disconnect();
    window.clearTimeout(completionTimerRef.current);
    lastCompletedAtRef.current = Date.now();
    setStatus('original');
    isApplyingRef.current = false;
  }, []);

  const waitForTranslateComplete = useCallback(() => {
    const activeTargetLanguage = activeTargetLanguageRef.current;

    if (!activeTargetLanguage) {
      return;
    }

    const failTranslation = () => {
      const select = getTranslateSelect();

      if (select) {
        select.value = '';
        triggerSelectChange(select);
      }

      restoreOriginalText();
      clearTranslateFilterMarks();
      blockPageTranslation();
      isApplyingRef.current = false;
      setStatus('translate');
      // setLanguage(DEFAULT_PAGE_LANGUAGE);
    };

    const arm = () => {
      completionObserverRef.current?.disconnect();
      window.clearTimeout(completionTimerRef.current);

      if (isPageTranslated()) {
        markTranslateComplete();
        return;
      }

      completionObserverRef.current = new MutationObserver(() => {
        if (isPageTranslated()) {
          markTranslateComplete();
        }
      });

      completionObserverRef.current.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'lang', 'style'],
        childList: true,
        subtree: true,
      });

      // Never mark complete blindly on timeout: if Google swallowed the
      // trigger (engine still booting), re-fire it a couple of times, and
      // otherwise put the button back instead of faking success.
      completionTimerRef.current = window.setTimeout(() => {
        completionObserverRef.current?.disconnect();

        if (isPageTranslated()) {
          markTranslateComplete();
          return;
        }

        const select = getTranslateSelect();

        if (select && completionAttemptsRef.current < MAX_TRANSLATE_TRIGGER_ATTEMPTS) {
          completionAttemptsRef.current += 1;
          select.value = activeTargetLanguage;
          triggerSelectChange(select);
          arm();
          return;
        }

        failTranslation();
      }, TRANSLATE_COMPLETE_TIMEOUT);
    };

    arm();
  }, [markTranslateComplete, restoreOriginalText]);

  const tryApplyCachedTranslation = useCallback(() => {
    const activeTargetLanguage = activeTargetLanguageRef.current;

    if (!activeTargetLanguage || !hasTranslatedOnceRef.current) {
      return false;
    }

    applyTranslateFilter(translateFilterRef.current);
    snapshotOriginalText();

    const leafUpdates: Array<[HTMLElement, string]> = [];
    const parentUpdates: Array<[HTMLElement, string]> = [];

    for (const element of trackedTextElementsRef.current) {
      const original = originalElementTextRef.current.get(element);

      if (!element.isConnected || original === undefined || !original.trim()) {
        continue;
      }

      const translated = elementTextCacheRef.current.get(original);

      if (translated === undefined) {
        return false;
      }

      leafUpdates.push([element, translated]);
    }

    for (const parent of trackedTextParentsRef.current) {
      const originalHtml = originalParentHtmlRef.current.get(parent);

      if (!parent.isConnected || originalHtml === undefined || !originalHtml.trim()) {
        continue;
      }

      const translatedHtml = parentHtmlCacheRef.current.get(originalHtml);

      if (translatedHtml === undefined) {
        return false;
      }

      parentUpdates.push([parent, translatedHtml]);
    }

    isApplyingRef.current = true;
    localStorage.setItem(TRANSLATE_STORAGE_KEY, 'true');
    localStorage.setItem(TARGET_LANGUAGE_STORAGE_KEY, activeTargetLanguage);
    leafUpdates.forEach(([element, translated]) => {
      element.textContent = translated;
    });
    parentUpdates.forEach(([parent, translatedHtml]) => {
      parent.innerHTML = translatedHtml;
    });
    document.documentElement.lang = activeTargetLanguage;
    markTranslateComplete();
    return true;
  }, [markTranslateComplete, snapshotOriginalText]);

  const applyTranslation = useCallback(
    (shouldRetry = true, resetRetryCount = false, forceRestart = false) => {
      const activeTargetLanguage = activeTargetLanguageRef.current;

      if (!activeTargetLanguage || isApplyingRef.current) {
        return;
      }

      if (resetRetryCount) {
        retryCountRef.current = 0;
      }

      // Replay the cached result when it still covers every text node on the
      // page — Google (and its API) is only needed for content never
      // translated before.
      if (tryApplyCachedTranslation()) {
        return;
      }

      const select = getTranslateSelect();

      if (!select) {
        loadTranslateScript();

        if (shouldRetry && retryCountRef.current < MAX_APPLY_RETRIES) {
          retryCountRef.current += 1;
          window.clearTimeout(retryTimerRef.current);
          retryTimerRef.current = window.setTimeout(() => applyTranslation(true, false, forceRestart), 400);
        } else if (status !== 'original') {
          // A user-initiated flow (status 'translating', or 'translate' when
          // replaying with an existing cache) gave up — revert fully,
          // including the locale a 'both' session already switched. The
          // background re-translate flow (status 'original') stays as is.
          setStatus('translate');

          if (activeEngineRef.current === 'both') {
            activeEngineRef.current = 'googleElement';
            setLanguage(DEFAULT_PAGE_LANGUAGE);
          }
        }
        return;
      }

      retryCountRef.current = 0;
      completionAttemptsRef.current = 0;
      isApplyingRef.current = true;
      unblockPageTranslation();
      applyTranslateFilter(translateFilterRef.current);
      snapshotOriginalText();
      preserveLineBreaksForTranslation();
      localStorage.setItem(TRANSLATE_STORAGE_KEY, 'true');
      localStorage.setItem(TARGET_LANGUAGE_STORAGE_KEY, activeTargetLanguage);
      setTranslateCookie(activeTargetLanguage);

      if (select.value === activeTargetLanguage && !forceRestart) {
        // This branch is the dynamic-content observer's path: the page is
        // already translated and new content just mounted. Google's own
        // observer usually handles it (bulk mutations like a modal opening
        // wake it), but it goes dormant on small isolated mutations and can
        // sit on them indefinitely (reproduced: an API error message stayed
        // untranslated 12s+ while the rest of the page was translated).
        // Never re-fire the combo unconditionally here: a change event
        // makes the engine revert-and-retranslate the whole page (every
        // open surface flashes back to the original language) and races
        // Google's own in-flight scan, leaving parts of freshly-stacked
        // modals untranslated. Instead, verify after a delay and nudge
        // ONLY when the new content is demonstrably stuck.
        window.clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = window.setTimeout(() => {
          const pending = pendingNudgeElementsRef.current;
          pendingNudgeElementsRef.current = [];

          if (statusRef.current !== 'original' || isRestoringRef.current) {
            return;
          }

          if (Date.now() - lastNudgeAtRef.current < OBSERVER_NUDGE_COOLDOWN) {
            return;
          }

          // Stuck = Google never touched the node at all: its textContent
          // is byte-identical to what it mounted with and no <font> wrapper
          // appeared anywhere inside. A node Google is actively working on
          // (or has partially translated — modals mid-scan) fails this
          // check, so the nudge can't race an in-flight scan. Require a
          // couple of letters so number/icon nodes can't trigger nudges,
          // and never count a node twice: text Google deliberately keeps
          // as-is (e.g. "OK") must not re-nudge on every later batch.
          const stuckNodes = pending.filter(
            ({ el, text }) =>
              el.isConnected &&
              text !== null &&
              /[a-zA-Z]{2,}/.test(text) &&
              (el.textContent || '') === text &&
              !el.querySelector('font') &&
              !shouldSkipElement(el) &&
              nudgeAttemptedTextRef.current.get(el) !== text
          );

          if (stuckNodes.length === 0) {
            return;
          }

          const combo = getTranslateSelect();

          if (combo && combo.value === activeTargetLanguageRef.current) {
            stuckNodes.forEach(({ el, text }) => nudgeAttemptedTextRef.current.set(el, text as string));
            lastNudgeAtRef.current = Date.now();
            triggerSelectChange(combo);
          }
        }, OBSERVER_NUDGE_VERIFY_DELAY);

        markTranslateComplete();
        return;
      }

      if (select.value === activeTargetLanguage && forceRestart) {
        select.value = '';
        triggerSelectChange(select);
      }

      window.setTimeout(() => {
        select.value = activeTargetLanguage;
        triggerSelectChange(select);
        waitForTranslateComplete();
      }, 100);
    },
    [
      loadTranslateScript,
      markTranslateComplete,
      setLanguage,
      snapshotOriginalText,
      status,
      tryApplyCachedTranslation,
      waitForTranslateComplete,
    ]
  );

  const restoreOriginal = useCallback(() => {
    // An i18n session never touched the DOM through Google — restoring is
    // just switching the dictionary locale back. No snapshots to replay, no
    // settle window needed (the locale re-render is React-owned), but a
    // queued google retry must still be cancelled.
    if (activeEngineRef.current === 'i18n') {
      window.clearTimeout(pendingTranslateTimerRef.current);
      activeEngineRef.current = 'googleElement';
      setLanguage(DEFAULT_PAGE_LANGUAGE);
      setStatus('translate');
      return;
    }

    isRestoringRef.current = true;
    completionObserverRef.current?.disconnect();
    window.clearTimeout(completionTimerRef.current);
    window.clearTimeout(retryTimerRef.current);
    window.clearTimeout(mutationTimerRef.current);
    window.clearTimeout(restoreTimerRef.current);
    window.clearTimeout(pendingTranslateTimerRef.current);
    window.clearTimeout(nudgeTimerRef.current);
    pendingNudgeElementsRef.current = [];

    isApplyingRef.current = false;
    localStorage.setItem(TRANSLATE_STORAGE_KEY, 'false');
    localStorage.removeItem(TARGET_LANGUAGE_STORAGE_KEY);
    clearTranslateCookie();

    // Capture the translation cache now: this is the only moment the page is
    // guaranteed to be in its final, fully-translated state (Google adds the
    // translated-* classes before it finishes every node, so capturing on a
    // timer risks caching half-translated content).
    if (isPageTranslated()) {
      captureTranslationCache();
    }

    // Mark the page notranslate BEFORE touching the DOM: the widget's
    // in-memory observer would otherwise re-translate every node we restore
    // below. Keep the widget itself alive — tearing it down cannot kill the
    // loaded engine, and re-initializing later stacks a second engine that
    // double-translates the page.
    blockPageTranslation();

    const select = getTranslateSelect();

    if (select && select.value) {
      select.value = '';
      triggerSelectChange(select);
    }

    restoreOriginalText();
    clearTranslateFilterMarks();
    document.documentElement.classList.remove('translated-ltr', 'translated-rtl');
    document.body.classList.remove('translated-ltr', 'translated-rtl');
    document.documentElement.lang = initialHtmlLangRef.current;

    // A 'both' session also switched the dictionary locale, and the DOM
    // snapshots just replayed contain the LOCALIZED labels — revert the
    // locale AFTER the restore so the re-render lands on the restored DOM.
    // (Single-text-child labels survive the google pass having detached the
    // original text nodes: React updates them via textContent on the parent
    // element, which stays connected.)
    if (activeEngineRef.current === 'both') {
      activeEngineRef.current = 'googleElement';
      setLanguage(DEFAULT_PAGE_LANGUAGE);
    }

    setStatus('translate');

    restoreTimerRef.current = window.setTimeout(() => {
      restoreOriginalText();
      document.documentElement.classList.remove('translated-ltr', 'translated-rtl');
      document.body.classList.remove('translated-ltr', 'translated-rtl');
      isRestoringRef.current = false;
    }, RESTORE_SETTLE_DELAY);
  }, [captureTranslationCache, restoreOriginalText, setLanguage]);

  const translatePage = useCallback(
    (useBrowserLanguage?: boolean, engine: TranslateEngine = 'googleElement') => {
      // Transient states: a restore is still settling (isRestoringRef stays
      // true for RESTORE_SETTLE_DELAY after status is already back to
      // 'translate') or a first pass is in flight. Auto-translate callers
      // fire exactly once (mount effect / one-shot timer), so dropping the
      // call here would leave their screen untranslated with nothing to
      // retry it — the dynamic-content observer only runs while translated.
      // Queue a retry instead; the states resolve on their own. This also
      // holds i18n requests back: a locale re-render landing inside the
      // restore settle window would be clobbered by the second
      // restoreOriginalText pass at RESTORE_SETTLE_DELAY.
      if (isRestoringRef.current || status === 'translating') {
        window.clearTimeout(pendingTranslateTimerRef.current);
        pendingTranslateTimerRef.current = window.setTimeout(
          () => translatePageRef.current(useBrowserLanguage, engine),
          PENDING_TRANSLATE_RETRY_DELAY
        );
        return;
      }

      // Already translated: new content is picked up by the dynamic-content
      // observer, and re-running a full pass here would snapshot translated
      // DOM as "original" and corrupt restore.
      if (status !== 'translate') {
        return;
      }

      // Undefined keeps the automatic mode (the same param > cookie >
      // browser > English priority I18nContext resolves its locale with);
      // a caller can still force either the browser language or the app's
      // default page language (no translation).
      const resolvedMode: 'auto' | 'browser' | 'default' =
        useBrowserLanguage === undefined ? languageMode : useBrowserLanguage ? 'browser' : 'default';
      const resolvedSourceLanguage =
        resolvedMode === 'browser' ? browserLanguage : resolvedMode === 'default' ? DEFAULT_PAGE_LANGUAGE : locale;
      const resolvedTargetLanguage = normalizeLanguage(resolvedSourceLanguage);

      if (!resolvedTargetLanguage) {
        return;
      }

      // Set synchronously: the timer below (and everything it triggers) must
      // see this language even though it fires before languageMode's
      // state update has re-rendered the component.
      activeTargetLanguageRef.current = resolvedTargetLanguage;

      if (resolvedMode !== languageMode) {
        setLanguageMode(resolvedMode);
      }

      // i18n engine: switch the dictionary locale and mark the session
      // translated — synchronous, nothing for the Google machinery to do.
      // The dynamic-content observer stays inert for this session (it checks
      // activeEngineRef), and restorePage/restoreOriginal switch the locale
      // back through their own i18n branch.
      if (engine === 'i18n') {
        activeEngineRef.current = 'i18n';
        setLanguage(resolvedTargetLanguage);
        setStatus('original');
        return;
      }

      activeEngineRef.current = engine;

      // Show the transient "Translating" state only for the first translation
      // — later triggers replay the cached result and complete instantly.
      if (!hasTranslatedOnceRef.current) {
        setStatus('translating');
      }

      // 'both' switches the I18n locale BEFORE translating: it re-renders the
      // app tree (I18nRerenderBoundary) so every I18n.t() label updates in
      // place, and Google then translates that refreshed DOM (the snapshots
      // taken by applyTranslation therefore contain the LOCALIZED labels —
      // restoreOriginal reverts the locale to match). The flush delay below
      // lets React commit the locale re-render before the snapshot.
      if (engine === 'both') {
        setLanguage(resolvedTargetLanguage);
      }
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = window.setTimeout(() => applyTranslation(true, true, true), LOCALE_RERENDER_FLUSH_DELAY);
    },
    [applyTranslation, browserLanguage, languageMode, locale, setLanguage, status]
  );
  translatePageRef.current = translatePage;

  const restorePage = useCallback(() => {
    // A restore request supersedes any queued auto-translate retry — without
    // this, a retry scheduled just before a screen closed would translate
    // the page it left behind.
    window.clearTimeout(pendingTranslateTimerRef.current);

    if (statusRef.current !== 'original' || isRestoringRef.current) {
      return;
    }

    restoreOriginal();
  }, [restoreOriginal]);

  useEffect(() => {
    // Only google-element sessions watch for late-mounted content — in an
    // i18n session new content renders through I18n.t() already localized,
    // and scheduling applyTranslation here would google-translate a page
    // that was deliberately not google-translated. activeEngineRef is a ref,
    // but it only ever changes together with a status transition, so reading
    // it when this effect re-runs on status is safe.
    if (status !== 'original' || activeEngineRef.current === 'i18n') {
      return undefined;
    }

    const observer = new MutationObserver(mutations => {
      if (isRestoringRef.current) {
        return;
      }

      if (Date.now() - lastCompletedAtRef.current < TRANSLATE_SETTLE_DELAY) {
        return;
      }

      let hasDynamicContent = false;

      // Remember the node AND its current text for the dormant-observer
      // verification (gotcha #18) scheduled by the applyTranslation call
      // below. The text is captured here because collectLeafTextElements
      // never returns its own root, so a single leaf-only node (e.g. an
      // error <div>text</div>) has no per-leaf snapshot to compare against.
      const collectNudgeCandidate = (el: HTMLElement) => {
        snapshotOriginalText(el);
        hasDynamicContent = true;

        if (pendingNudgeElementsRef.current.length < OBSERVER_NUDGE_MAX_CANDIDATES) {
          pendingNudgeElementsRef.current.push({ el, text: el.textContent });
        }
      };

      const isBlockedElement = (el: HTMLElement) =>
        el.closest('.browser-page-translator') ||
        el.closest('.skiptranslate') ||
        el.closest('.notranslate') ||
        el.classList.contains('translated-ltr') ||
        el.classList.contains('translated-rtl') ||
        el.classList.contains('goog-te-gadget') ||
        el.classList.contains('goog-te-banner-frame') ||
        el.tagName === 'IFRAME';

      // Text-level mutations: React swapping a label in place (e.g. the
      // event-create footer button flipping to Btn.Select) replaces the
      // text node inside an already-translated element — no HTMLElement is
      // added, so the element branch below never sees it, and Google's
      // dormant observer sits on it exactly like on mounted elements. Track
      // the PARENT as a nudge candidate. Text Google writes itself lives
      // inside <font> wrappers, so the closest('font') check keeps the
      // engine's own output from re-entering the pipeline (loop guard).
      const collectTextMutation = (textNode: Node | null) => {
        const parent = textNode?.parentElement;

        if (!parent || parent.closest('font') || isBlockedElement(parent) || !textNode?.nodeValue?.trim()) {
          return;
        }

        collectNudgeCandidate(parent);
      };

      mutations.forEach(mutation => {
        if (mutation.type === 'characterData') {
          collectTextMutation(mutation.target);
          return;
        }

        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
          return;
        }

        Array.from(mutation.addedNodes).forEach(node => {
          if (node instanceof Text) {
            collectTextMutation(node);
            return;
          }

          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (isBlockedElement(node) || node.tagName === 'FONT') {
            return;
          }

          if (node.textContent?.trim()) {
            collectNudgeCandidate(node);
          }
        });
      });

      if (!hasDynamicContent) {
        return;
      }

      window.clearTimeout(mutationTimerRef.current);
      mutationTimerRef.current = window.setTimeout(() => applyTranslation(true, false, false), 700);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      window.clearTimeout(mutationTimerRef.current);
      window.clearTimeout(retryTimerRef.current);
    };
  }, [applyTranslation, snapshotOriginalText, status]);

  // The target can become null while the page is still translated (the
  // user's language changing to one that no longer needs translation, or a
  // logout switching the source to a browser language that doesn't) — put
  // the page back to its original language first.
  useEffect(() => {
    if (!targetLanguage && status !== 'translate') {
      restoreOriginal();
    }
  }, [restoreOriginal, status, targetLanguage]);

  // The cached translations are language-specific: throw them away whenever
  // the user's language changes so they are rebuilt for the new language.
  useEffect(() => {
    elementTextCacheRef.current = new Map();
    parentHtmlCacheRef.current = new Map();
    hasTranslatedOnceRef.current = false;
  }, [targetLanguage]);

  useEffect(() => {
    patchDomMethodsForTranslation();

    return () => {
      completionObserverRef.current?.disconnect();
      window.clearTimeout(completionTimerRef.current);
      window.clearTimeout(mutationTimerRef.current);
      window.clearTimeout(retryTimerRef.current);
      window.clearTimeout(restoreTimerRef.current);
      window.clearTimeout(pendingTranslateTimerRef.current);
      window.clearTimeout(nudgeTimerRef.current);
    };
  }, []);

  const value = useMemo(
    () => ({
      isAvailable: isLogin && targetLanguage !== null,
      isReady,
      status,
      targetLanguage,
      translatePage,
      restorePage,
      setTranslateFilter,
      defaultLanguage: DEFAULT_PAGE_LANGUAGE,
      browserLanguage,
    }),
    [isLogin, isReady, restorePage, setTranslateFilter, status, targetLanguage, translatePage]
  );

  return (
    <TranslateContext.Provider value={value}>
      {/* The widget's host element must always exist (and never remount) so
          the engine initializes exactly once per page load. */}
      <div
        id={GOOGLE_TRANSLATE_ELEMENT_ID}
        className="skiptranslate"
        translate="no"
        style={{ height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none', position: 'absolute', width: 0 }}
      />
      {children}
    </TranslateContext.Provider>
  );
};
